// src/pages/CaseBriefPage.js
//
// Aug 2026 redesign, v3 — see advisor_dashboard_case_brief_framework.md.
//
// Principle: facts, not interpretation. This page presents what the
// platform has confirmed — sourced to where it came from — and never
// tells the advisor what to do about it. The AI-generated narrative layer
// (situation_summary, confidence score, freeform cross-border prose) is
// not rendered. Deterministic rule-engine output (obligations/findings,
// data_gaps, data_conflicts, calculator classification) is NOT
// interpretation — it's deriveFacts/runRules output against the confirmed
// profile — so it's kept.
//
// v3 architecture change: the calculator is the primary/wide column
// whenever it exists — it's structured, economically concrete data, not a
// peer source to Snapshot's intake answers. Snapshot becomes context that
// fills gaps around it (assets, filing history, profile facts not
// re-asked by the calculator) rather than a second copy of the same
// income list. If there's no calculator run, Snapshot expands to fill the
// page on its own rather than sitting in a half-empty narrow column.
//
// Also v3: "Main issue cluster" (a derived shorthand) is gone from the
// header entirely (ticket 011 dropped the "Products completed" fact that
// had briefly replaced it too — see products.{triage,calculator}, still
// used to gate the Calculator/Snapshot columns, just not shown as a
// header row). The four stat cards from v2 are gone too — they duplicated
// what the columns
// below already show, at the cost of feeling like a dashboard rather than
// a case file. "Upfront items" + "Spain Findings" + the US coordination
// accordion are merged into one "Tax work" section (Setup / Spain filings
// / US coordination) — the old split implied some items were forms, some
// were tasks, some were something else, when an advisor just needs one
// place to see what's outstanding. The long AEAT "Basis" justification
// column is dropped from every findings table — an advisor doesn't need
// tax law explained back to them; the short line2 subtitle already names
// what each line is.

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { createClient } from '@supabase/supabase-js'
import { useAuth } from '../context/AuthContext'
import { getSlotLabel, getSlotReportLabel } from '../lib/slotConfig'
import { cleanBasis, formatEuro } from '../lib/briefUtils'
import { getAdvisorProductLabel, getAdvisorSessionStateLabel } from '../components/HomeLink'
import { INCOME_CARD_CONFIG } from '../hooks/incomeCardConfig'
import { BASE_LABELS } from '../components/TaxBaseExplainer'
import AdvisorIntakePanel from '../components/advisor/AdvisorIntakePanel'
import { ADVISOR_INTAKE_ONLY_NOTE } from '../lib/advisorIntakeModel'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

// ── Finding display metadata ───────────────────────────────────────────────────
// line1: short obligation identifier
// line2: concise conclusion (more informative than just "applies")

const FINDING_META = {
  irpf:               { line1: 'IRPF',                    line2: 'Modelo 100 — worldwide income reporting' },
  modelo_210:         { line1: 'Modelo 210 (IRNR)',       line2: 'Non-resident Spain-source income reporting' },
  modelo_720:         { line1: 'Modelo 720',              line2: 'Foreign asset declaration' },
  patrimonio:         { line1: 'Patrimonio',              line2: 'Modelo 714 — regional wealth tax' },
  itsgf:              { line1: 'ITSGF',                   line2: 'Modelo 718 — solidarity tax on large fortunes' },
  autonomo:           { line1: 'Autónomo / RETA',         line2: 'Self-employment + social security obligations' },
  autonomo_setup:     { line1: 'Autónomo registration',   line2: 'RETA + AEAT — one-time setup before starting work' },
  first_time_filer:   { line1: 'First-time filing setup', line2: 'NIE / AEAT registration' },
  beckham_active:     { line1: 'Beckham Law',             line2: 'Modelo 151 — special expat tax regime' },
  capital_gains:      { line1: 'Capital gains',           line2: 'Spain savings base (ahorro) reporting' },
  equity_compensation:{ line1: 'Equity compensation',    line2: 'RSU / option cross-border apportionment' },
  inheritance:        { line1: 'Inheritance / gift',      line2: 'Impuesto de Sucesiones / foreign reporting' },
  us_1040:            { line1: 'US Form 1040',            line2: 'Federal return — worldwide income' },
  fbar:               { line1: 'FBAR',                    line2: 'FinCEN 114 — foreign account reporting' },
  feie_ftc:           { line1: 'FEIE / FTC',              line2: 'Form 2555 / 1116 — foreign income election' },
  pfic:               { line1: 'PFIC',                    line2: 'Form 8621 — passive foreign investment companies' },
  us_rental:          { line1: 'US rental income',        line2: 'Schedule E — US rental property' },
}

function getFindingDisplay(f) {
  const meta = FINDING_META[f.id]
  return {
    line1: meta?.line1 || f.obligation,
    line2: meta?.line2 || null,
  }
}

// ── Contact display — email + phone (D5, ticket 008) ────────────────────────
// Same pattern for both: a link (mailto:/tel:) + a copy-to-clipboard button.
// Reuses .brief-header-email/.brief-header-email-link/.brief-header-copy-btn
// as-is — named as if email-specific but generic enough for phone too; a
// rename pass is a fast-follow if a third contact method ever shows up here.

function ContactDisplay({ value, href, copyTitle }) {
  const [copied, setCopied] = useState(false)

  function copyValue(e) {
    e.preventDefault()
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="brief-header-email">
      <a href={href} className="brief-header-email-link">{value}</a>
      <button className="brief-header-copy-btn" onClick={copyValue} title={copyTitle}>
        {copied ? '✓' : '⧉'}
      </button>
    </div>
  )
}

function EmailDisplay({ email }) {
  return <ContactDisplay value={email} href={`mailto:${email}`} copyTitle="Copy email address" />
}

function PhoneDisplay({ phone }) {
  return <ContactDisplay value={phone} href={`tel:${phone}`} copyTitle="Copy phone number" />
}

// findings' applies status is deterministic rule-engine output (runRules),
// not an AI judgment call — kept as a plain status label.
function mapStatus(applies) {
  switch (applies) {
    case 'yes':      return { label: 'Applies',        cls: 'brief-status-applies' }
    case 'no':       return { label: 'Not applicable', cls: 'brief-status-not-applicable' }
    case 'possible': return { label: 'Conditional',    cls: 'brief-status-conditional' }
    case 'unknown':  return { label: 'Needs review',   cls: 'brief-status-needs-review' }
    default:         return { label: 'Needs review',   cls: 'brief-status-needs-review' }
  }
}

// ── Jurisdiction helper ───────────────────────────────────────────────────────

const SPAIN_IDS = new Set(['irpf','modelo_210','modelo_720','patrimonio','itsgf','autonomo','autonomo_setup','first_time_filer','beckham_active','capital_gains','equity_compensation','inheritance'])
const US_IDS    = new Set(['us_1040','fbar','feie_ftc','pfic','us_rental'])

function getJurisdiction(f) {
  if (f.jurisdiction) return f.jurisdiction
  if (SPAIN_IDS.has(f.id)) return 'spain'
  if (US_IDS.has(f.id))    return 'us'
  return 'both'
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(d)     { if (!d) return '—'; return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
function formatDateTime(d) { if (!d) return '—'; return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) }
function formatMoveDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) }
function statusLabel(s)    { return { new: 'New', contacted: 'Contacted', converted: 'Converted', not_a_fit: 'Not a fit' }[s] || s }

// Renders a multi-select fact as a plain label string when there's exactly
// one value (a one-item bullet list is pointless), or a real <ul> when
// there's more than one. Shared by Real estate holdings and Major events so
// the list-vs-string decision is made once, not per field.
function buildFactListValue(items, slotKey) {
  const labels = items.map(v => ({ v, label: getSlotReportLabel(slotKey, v) || v }))
  if (labels.length <= 1) return labels[0]?.label ?? null
  return (
    <ul className="brief-fact-list">
      {labels.map(({ v, label }) => <li key={v}>{label}</li>)}
    </ul>
  )
}

// ── Adapter ───────────────────────────────────────────────────────────────────

function buildSections(brief, subject, tp, yp, email, phone, triagePublicToken) {
  const taxYear = brief.tax_year
  const s       = brief.subject || {}

  const comunidad = s.comunidad || tp?.comunidad_autonoma
  const products  = subject.products || { triage: false, calculator: false }

  const allFindings = brief.obligations || []

  // ── Setup tasks (was "Upfront items," now folds into Tax work) ───────────
  // 'autonomo' (the ongoing RETA/quarterly-filing obligation, distinct
  // from 'autonomo_setup' — the one-time registration) added per 2026-08
  // feedback: both read as "setup," not a distinct form. UPFRONT_ACTION_TEXT
  // has no entry for it — falls through to cleanBasis(f.basis), same as any
  // future id added here without custom text.
  const UPFRONT_IDS = new Set(['autonomo_setup', 'first_time_filer', 'autonomo'])
  const UPFRONT_ACTION_TEXT = {
    autonomo_setup:   'Register with RETA (Social Security) + AEAT before starting work',
    first_time_filer: 'Needs NIE + AEAT registration',
  }
  const setupTasks = allFindings
    .filter(f => UPFRONT_IDS.has(f.id) && f.applies === 'yes')
    .map(f => ({ label: getFindingDisplay(f).line1, value: UPFRONT_ACTION_TEXT[f.id] || cleanBasis(f.basis) }))

  if (s.beckham_status === 'not_active') {
    setupTasks.push({
      label: 'Beckham Law',
      value: 'Not enrolled — confirm eligibility if recently arrived',
    })
  }

  // ── Header ─────────────────────────────────────────────────────────────
  // Filing history — promoted to the header so Spain Snapshot can stay a
  // pure asset view. Prior Spain filing + filing goal on one line per the
  // 2026-08 redesign feedback; prior gestor folds in too since it's the
  // same "history" fact, not an asset.
  const filingHistoryParts = [
    yp?.filed_spain_before != null ? (yp.filed_spain_before ? 'Prior Spain filing' : 'No prior Spain filing') : null,
    yp?.has_gestor != null ? (yp.has_gestor ? 'Used a gestor before' : 'No prior gestor') : null,
    yp?.filing_goal ? getSlotReportLabel('filing_goal', yp.filing_goal) : null,
  ].filter(Boolean)

  // Calculator estimate, needed here (not just further below) so the
  // header's Family status row can gate its dependent clauses on whether a
  // calculator run exists — calculator-only data, no fallback source (D4).
  // Reassigned/read again further down for the income/filing-comparison
  // rendering.
  const calc = brief.calculator_estimate || null

  const maritalStatus     = s.marital_status || tp?.marital_status
  const citizenshipStatus = s.citizenship_status || tp?.citizenship_status

  // Family status — combines marital status with dependent counts from the
  // calculator's dependents object into one row (2026-09 addendum, ticket
  // 008). Disability status is deliberately excluded — not part of this
  // clause. Children/dependent clauses only appear when a calculator run
  // exists AND the count is > 0; a bare marital status (or nothing, if
  // neither fact exists) renders with no broken/empty fragment.
  const familyStatusParts = []
  if (maritalStatus) familyStatusParts.push(getSlotLabel('marital_status', maritalStatus))
  if (calc) {
    const childrenCount  = calc.dependents?.children_under_25 ?? 0
    const dependents65Count = calc.dependents?.ascendants_over_65 ?? 0
    if (childrenCount > 0) {
      familyStatusParts.push(`${childrenCount} ${childrenCount === 1 ? 'child' : 'children'}`)
    }
    if (dependents65Count > 0) {
      familyStatusParts.push(`${dependents65Count} 65+ ${dependents65Count === 1 ? 'dependent' : 'dependents'}`)
    }
  }

  // ── Header — one flat merged list, replacing the old header summary +
  // separate "Profile" box (2026-09 cleanup, ticket 008). Every fact is its
  // own row instead of a row *and* a fragment of a combined string — that
  // combined "Tax year · Region" string was the source of the duplication
  // this redesign removes. Fixed order per the ticket's plan (D1); not
  // attributed to one product since either product's claim-session gap-fill
  // can populate these facts. ─────────────────────────────────────────────
  const header = {
    name:        s.display_name || subject.display_name,
    email,
    phone,
    triagePublicToken,
    leadStatus:  subject.lead_status || 'new',
    assignedAt:  subject.lead_assigned_at,
    summaryRows: [
      taxYear ? { label: 'Tax year', value: String(taxYear) } : null,
      comunidad ? { label: 'Comunidad Autónoma', value: getSlotLabel('comunidad_autonoma', comunidad) } : null,
      (yp?.spain_residency_status || s?.residency_status || s.move_date || tp?.move_date)
        ? { label: `Spain residency (${taxYear})`, value: [
            (yp?.spain_residency_status || s?.residency_status) ? getSlotReportLabel('spain_residency_status', yp?.spain_residency_status || s?.residency_status) : null,
            (s.move_date || tp?.move_date) ? `Arrived ${formatMoveDate(s.move_date || tp?.move_date)}` : null,
          ].filter(Boolean).join(' · ') }
        : null,
      citizenshipStatus
        ? { label: 'Citizenship', value: getSlotLabel('citizenship_status', citizenshipStatus) }
        : null,
      familyStatusParts.length > 0
        ? { label: 'Family status', value: familyStatusParts.join(' · ') }
        : null,
      (s.beckham_active || tp?.beckham_status)
        ? { label: 'Beckham Law', value: s.beckham_active ? 'Active / approved' : getSlotReportLabel('beckham_status', tp?.beckham_status) }
        : null,
      filingHistoryParts.length > 0
        ? { label: 'Filing history', value: filingHistoryParts.join(' · ') }
        : null,
    ].filter(Boolean),
  }

  // ── Income facts — only rendered when there's no calculator run to
  // supersede them (see render logic) ───────────────────────────────────────
  const incomeFacts = (yp?.income_types || [])
    .filter(t => t !== 'none')
    .map(t => {
      const typeLabel = getSlotReportLabel('income_types', t) || t
      let detail = null
      if (t === 'self_employment' && yp?.self_employment_country) {
        detail = getSlotReportLabel('self_employment_country', yp.self_employment_country)
      } else if (t === 'rental' && yp?.rental_property_location) {
        detail = getSlotReportLabel('rental_property_location', yp.rental_property_location)
      } else if ((t === 'employment_es' || t === 'employment_us') && yp?.employment_location_when_earned) {
        detail = getSlotReportLabel('employment_location_when_earned', yp.employment_location_when_earned)
      } else if (t === 'pension' && yp?.pension_source) {
        detail = [
          getSlotReportLabel('pension_source', yp.pension_source),
          yp?.pension_is_government != null ? getSlotReportLabel('pension_is_government', yp.pension_is_government) : null,
        ].filter(Boolean).join(' · ')
      } else if (t === 'business_partnership' && yp?.business_partnership_type) {
        detail = getSlotReportLabel('business_partnership_type', yp.business_partnership_type)
      } else if (t === 'investments') {
        // Static category description, not a per-subject fact — no triage
        // sub-question exists for investments (see Tier 3,
        // tickets/017-incomedetail/ticket.md). Don't confuse this with the
        // branches above, which all read a real confirmed field.
        detail = 'Dividends, interest or capital gains'
      }
      return { label: typeLabel, value: detail || 'Reported' }
    })

  // ── Assets — genuinely Snapshot-exclusive (the calculator flow never
  // asks about foreign accounts / net worth / major events / real estate) ──
  const assetFacts = [
    yp?.foreign_account_band
      ? { label: 'Foreign accounts / assets', value: getSlotLabel('foreign_account_band', yp.foreign_account_band) }
      : null,
    yp?.worldwide_net_worth_band
      ? { label: 'Estimated net worth', value: getSlotLabel('worldwide_net_worth_band', yp.worldwide_net_worth_band) }
      : null,
    tp?.asset_split && ['married','civil_partnership'].includes(maritalStatus)
      ? { label: 'Asset split', value: getSlotReportLabel('asset_split', tp.asset_split) }
      : null,
    yp?.property_ownership?.length > 0
      ? { label: 'Real estate holdings', value: buildFactListValue(yp.property_ownership, 'property_ownership') }
      : null,
    yp?.major_events?.length > 0
      ? { label: 'Major events', value: buildFactListValue(yp.major_events, 'major_events') }
      : null,
  ].filter(Boolean)

  // ── Tax work — Spain filings only (Basis column dropped, US coordination
  // removed entirely per 2026-08 feedback). Ids already routed to Setup
  // above are excluded here — that's the fix for the duplication bug: this
  // filter used to be jurisdiction-only, so autonomo_setup/first_time_filer
  // (both jurisdiction 'spain') landed in both Setup and this table.
  // capital_gains dropped too — it's a tax treatment applied within the
  // IRPF filing itself, not a distinct form; the fact that triggers it
  // (a large investment sale) is already visible in Snapshot's Assets ->
  // Major events, so nothing is lost, just de-duplicated.
  const EXCLUDED_FROM_SPAIN_FILINGS = new Set([...UPFRONT_IDS, 'capital_gains'])
  const spainFindings = allFindings
    .filter(f => !EXCLUDED_FROM_SPAIN_FILINGS.has(f.id))
    .filter(f => { const j = getJurisdiction(f); return j === 'spain' || j === 'both' })
    .sort((a, b) => ({ yes: 0, possible: 1, unknown: 2, no: 3 }[a.applies] ?? 3) - ({ yes: 0, possible: 1, unknown: 2, no: 3 }[b.applies] ?? 3))
    .map(f => ({ ...getFindingDisplay(f), status: mapStatus(f.applies) }))

  const dataGaps  = brief.data_gaps || []
  const spainGaps = dataGaps.filter(g => g.jurisdiction === 'spain' || g.jurisdiction === 'both')

  // ── Calculator — income split by owner (self/spouse) when a filing
  // comparison exists, then grouped by Spanish tax base within each —
  // classification shown once as the group heading instead of repeated
  // per row. Unmarried / no-spouse-items sessions get a single unlabeled
  // "self" group so the owner heading doesn't show for solo filers.
  // (`calc` itself is already computed above, for the header's Family status row.)

  function groupByBase(items) {
    const order = ['general', 'savings', 'excluded', 'uncertain']
    const buckets = {}
    items.forEach(item => {
      const key = item.spanish_tax_base || 'uncertain'
      ;(buckets[key] = buckets[key] || []).push(item)
    })
    return order.filter(k => buckets[k]?.length > 0).map(k => ({ base: k, items: buckets[k] }))
  }

  let incomeByOwner = null
  if (calc?.income_items?.length > 0) {
    const hasSpouseItems = calc.income_items.some(i => i.owner === 'spouse')
    if (hasSpouseItems) {
      const selfItems   = calc.income_items.filter(i => (i.owner || 'self') === 'self')
      const spouseItems = calc.income_items.filter(i => i.owner === 'spouse')
      incomeByOwner = [
        selfItems.length > 0   ? { owner: 'self',   label: 'You',    groups: groupByBase(selfItems) }   : null,
        spouseItems.length > 0 ? { owner: 'spouse', label: 'Spouse', groups: groupByBase(spouseItems) } : null,
      ].filter(Boolean)
    } else {
      incomeByOwner = [{ owner: 'self', label: null, groups: groupByBase(calc.income_items) }]
    }
  }

  return {
    header,
    products,
    setupTasks,
    assetFacts,
    incomeFacts,
    calculatorEstimate: calc,
    incomeByOwner,
    filingComparison: calc?.filing_comparison || null,
    dataConflicts: brief.data_conflicts || [],
    spainFindings,
    unconfirmedFacts: spainGaps,
    advisorNotes: brief.advisor_notes || null,
  }
}

// ── Section renderers ──────────────────────────────────────────────────────────

function BriefSection({ title, children }) {
  return (
    <div className="brief-section">
      <div className="brief-section-header">
        <div className="brief-section-title">{title}</div>
      </div>
      <div className="brief-section-body">{children}</div>
    </div>
  )
}

function FactGroup({ title, facts }) {
  if (!facts || facts.length === 0) return null
  return (
    <div className="brief-fact-group">
      {title && <div className="brief-fact-group-title">{title}</div>}
      {facts.map(({ label, value }) => (
        <div key={label} className="brief-fact-row">
          <span className="brief-fact-label">{label}</span>
          <div className="brief-fact-value">{value}</div>
        </div>
      ))}
    </div>
  )
}

// Form + Status only — no Basis column. line2 (short subtitle) still
// carries just enough context to identify the item without re-explaining
// the rule to a professional.
function FindingsTable({ findings }) {
  if (!findings || findings.length === 0) {
    return <p className="brief-empty-note">No findings available.</p>
  }
  return (
    <div className="brief-findings-wrap">
      <table className="brief-findings-table">
        <thead>
          <tr>
            <th className="brief-findings-th">Form</th>
            <th className="brief-findings-th">Status</th>
          </tr>
        </thead>
        <tbody>
          {findings.map((f, i) => (
            <tr key={i} className="brief-findings-tr">
              <td className="brief-findings-td">
                <div className="brief-findings-area">{f.line1}</div>
                {f.line2 && <div className="u-desc-xs">{f.line2}</div>}
              </td>
              <td className="brief-findings-td brief-findings-td-nowrap">
                <span className={`brief-status ${f.status.cls}`}>{f.status.label}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// `link` (optional): { href, label } — "View as client saw it ↗", the
// public-token link to what the client actually saw/submitted (D2). Kept
// distinct from ScenariosCard's "View scenario →" in-app peek button —
// external client-facing view vs. in-app scenario switch are different
// actions and must read as such.
function SourceCardHeader({ title, sub, completed, badge = 'Completed', badgeClass = '', link }) {
  return (
    <div className="brief-source-card-header">
      <div>
        <div className="brief-source-card-title">{title}</div>
        {sub && <div className="brief-source-card-sub">{sub}</div>}
        {link && (
          <a href={link.href} target="_blank" rel="noopener noreferrer" className="u-text-link">
            {link.label}
          </a>
        )}
      </div>
      {completed && <span className={`brief-source-card-badge${badgeClass ? ` ${badgeClass}` : ''}`}>{badge}</span>}
    </div>
  )
}

// ── Calculator column — primary, wide ────────────────────────────────────────

const REVIEW_FLAG_LABELS = {
  treaty_review:                  'Treaty review',
  foreign_retirement_account:     'Foreign retirement acct.',
  tax_advantaged_account_review:  'Tax-advantaged acct.',
  entity_classification_review:   'Entity classification',
  foreign_company_distribution:   'Foreign company dist.',
  pass_through_income_review:     'Pass-through review',
  foreign_government_pension:     'Govt. pension (treaty)',
  foreign_tax_relief:             'Foreign tax relief',
  owner_operated_entity:          'Owner-operated entity',
  partial_year_residency:         'Partial-year residency',
  currency_conversion:            'Currency conversion',
  property_sale_exemption:        'Primary residence exemption?',
  rental_reduction_eligibility:   'Rental reduction (60%)?',
  crypto_reporting_review:        'Crypto reporting',
  professional_review_required:  'Needs review',
  us_roth_treatment:              'Roth treatment (contested)',
  us_llc_k1_classification:       'LLC / K-1 (two scenarios)',
}

// Derived at import time from incomeCardConfig.js's INCOME_CARD_CONFIG —
// the same canonical source IncomeCard.js's subtype dropdown reads
// (cardConfig.subtypeOptions) — instead of a hand-typed mirror. This
// closes the drift the two used to have (tickets/017-incomedetail):
// roughly half the entries had diverged, several losing real meaning
// (e.g. a foreign pension label that could read as government when it
// meant the opposite).
//
// other_foreign_retirement is the one real IncomeSubtype (calculatorTypes.ts)
// with no incomeCardConfig.js dropdown entry — it's assigned by the
// classifier (incomeClassificationRules.ts), never chosen by the user, so
// it can't be derived from the flatten. Kept as an explicit override so
// items carrying it still get a real label instead of falling through to
// the raw-value auto-fallback.
const INCOME_SUBTYPE_LABELS = {
  ...Object.fromEntries(
    INCOME_CARD_CONFIG.flatMap(c => c.subtypeOptions || []).map(o => [o.value, o.label])
  ),
  other_foreign_retirement: 'Other foreign retirement acct.',
}

// Fallback for items with no income_subtype (subtype is optional — see
// calculatorTypes.ts) — mirrors the IncomeType enum. Without this, a
// user-entered label like "Chase Bank" or "Slava" tells an advisor
// nothing about what kind of income it actually is; income_type is always
// present even when subtype isn't, so it's the right floor to fall back
// to rather than showing nothing.
//
// Derived from incomeCardConfig.js the same way as INCOME_SUBTYPE_LABELS
// above (same drift class, same fix). 'interest' has no dedicated card in
// incomeCardConfig.js (folded into the 'dividends' card's UI copy,
// "Dividends and interest") but is still a real IncomeType the rules
// engine matches on (incomeClassificationRules.ts:268) — kept as an
// explicit override for the same reason as other_foreign_retirement above.
const INCOME_TYPE_FALLBACK_LABELS = {
  ...Object.fromEntries(INCOME_CARD_CONFIG.map(c => [c.income_type, c.label])),
  interest: 'Interest / savings',
}

// self_employment items have no entry in the IncomeSubtype enum —
// activity_classification is their real "more specifically" fact.
const ACTIVITY_CLASSIFICATION_LABELS = {
  profesional:                   'Profesional activity',
  empresarial:                   'Empresarial activity',
  agricola_ganadera_forestal:    'Agrícola / ganadera / forestal activity',
}

// Foreign tax paid is in the item's original currency, not EUR — showing
// a bare "€X" would misrepresent it. Shown as a plain labeled amount
// instead of running it through formatEuro.
function formatForeignTaxPaid(amount, precision) {
  if (precision === 'unknown') return 'foreign tax paid: unknown'
  if (!amount) return null
  return `${amount.toLocaleString('en-US')} foreign tax paid`
}

// Grouped by tax base at the call site — this table just renders one
// group's rows, no classification column (it's the group heading now).
// Metadata line: subtype (e.g. "US LLC / K-1"), source country, taxes
// already paid (foreign + Spain withholding), then review flags — mirrors
// what generate-case-brief now forwards from ClassifiedIncomeItem.
function GroupedIncomeTable({ items }) {
  const subtotal = items.reduce((sum, item) => sum + (item.amount_eur || 0), 0)
  return (
    <div className="brief-findings-wrap">
      <table className="brief-findings-table brief-income-table">
        <tbody>
          {items.map((item, i) => {
            const flagLabels = (item.review_flags || []).map(f => REVIEW_FLAG_LABELS[f] || f.replace(/_/g, ' '))
            // "More specifically" descriptor — always show something, even
            // when the item has no income_subtype and the user's own label
            // is ambiguous ("Chase Bank," "Slava"). self_employment items
            // use activity_classification instead (subtype doesn't cover
            // them); everything else falls back to income_type.
            const specificLabel = item.income_type === 'self_employment'
              ? (item.activity_classification
                  ? (ACTIVITY_CLASSIFICATION_LABELS[item.activity_classification] || item.activity_classification.replace(/_/g, ' '))
                  : (INCOME_TYPE_FALLBACK_LABELS.self_employment))
              : item.income_subtype
                ? (INCOME_SUBTYPE_LABELS[item.income_subtype] || item.income_subtype.replace(/_/g, ' '))
                : (INCOME_TYPE_FALLBACK_LABELS[item.income_type] || item.income_type?.replace(/_/g, ' ') || null)
            const metaParts = [
              specificLabel,
              item.source_country || null,
              formatForeignTaxPaid(item.tax_paid_foreign, item.tax_paid_precision),
              item.tax_withheld_spain > 0 ? `${formatEuro(item.tax_withheld_spain)} withheld in Spain` : null,
            ].filter(Boolean)
            return (
              <tr key={i} className="brief-findings-tr">
                <td className="brief-findings-td">
                  {item.label}
                  {metaParts.length > 0 && <div className="u-desc-xs">{metaParts.join(' · ')}</div>}
                  {flagLabels.length > 0 && <div className="u-desc-xs">{flagLabels.join(', ')}</div>}
                </td>
                <td className="brief-findings-td brief-findings-td-nowrap">{formatEuro(item.amount_eur)}</td>
              </tr>
            )
          })}
        </tbody>
        {items.length > 1 && (
          <tfoot>
            <tr className="brief-findings-tr brief-findings-total-row">
              <td className="brief-findings-td"><strong>Subtotal</strong></td>
              <td className="brief-findings-td brief-findings-td-nowrap"><strong>{formatEuro(subtotal)}</strong></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

// Only rendered when display_mode === 'adjustment_estimate' — the headline
// irpf_gross is a conservative baseline, and each of these is a specific,
// deterministic condition that would lower it if it holds. Deterministic
// engine output (irpfCore.ts), same standing as everything else on this
// page — not a recommendation, just what the engine already computed.
function AdjustmentItemsTable({ items }) {
  if (!items || items.length === 0) return null
  return (
    <div className="brief-adjustments">
      <div className="brief-fact-group-title">Potential reductions to this baseline</div>
      {items.map((a, i) => (
        <div key={i} className="brief-adjustment-row">
          <div className="brief-adjustment-top">
            <span className="brief-adjustment-label">{a.label}</span>
            <span className="brief-adjustment-reduction">−{formatEuro(a.potential_reduction)}</span>
          </div>
          <div className="u-desc-xs">{a.condition}</div>
          {a.advisor_note && <div className="brief-adjustment-note">{a.advisor_note}</div>}
        </div>
      ))}
    </div>
  )
}

// Only present when the calculator ran the individual-vs-joint comparison
// (married + at least one spouse-tagged item) — see filingComparison.ts.
// Mirrors the consumer result page's FilingComparisonCard: recommendation
// first, then both household totals, with the individual total broken
// into "your return + spouse's return" so it doesn't read as a third,
// disagreeing number next to the headline estimate.
function FilingComparisonNote({ comparison }) {
  if (!comparison) return null
  const jointRecommended = comparison.recommended_mode === 'joint_orientation_v1'
  const hasSplit = comparison.self_irpf_gross != null && comparison.spouse_irpf_gross != null

  return (
    <div className="brief-filing-comparison">
      <div className="brief-fact-group-title">Filing comparison</div>
      <div className="brief-fact-row">
        <span className="brief-fact-label">Recommended</span>
        <span className="brief-fact-value">{jointRecommended ? 'Filing jointly' : 'Filing individually'}</span>
      </div>
      <div className="brief-fact-row">
        <span className="brief-fact-label">Filing individually</span>
        <span className="brief-fact-value">
          {formatEuro(comparison.household_tax_individual)}
          {hasSplit && (
            <span className="u-desc-xs"> (You: {formatEuro(comparison.self_irpf_gross)} + spouse: {formatEuro(comparison.spouse_irpf_gross)})</span>
          )}
        </span>
      </div>
      <div className="brief-fact-row">
        <span className="brief-fact-label">Filing jointly</span>
        <span className="brief-fact-value">{formatEuro(comparison.household_tax_joint)}</span>
      </div>
      {comparison.savings_vs_alternative > 0 && (
        <div className="brief-fact-row">
          <span className="brief-fact-label">Savings from recommended mode</span>
          <span className="brief-fact-value">{formatEuro(comparison.savings_vs_alternative)}</span>
        </div>
      )}
      {comparison.assumptions?.length > 0 && (
        <div className="u-desc-xs brief-filing-comparison-assumptions">{comparison.assumptions.join(' ')}</div>
      )}
    </div>
  )
}

function ScenariosCard({ sessions, viewedSessionId, onSelect, loading }) {
  if (!sessions || sessions.length <= 1) return null
  const defaultId = sessions[0]?.id
  const activeId = viewedSessionId || defaultId

  return (
    <div className="brief-source-card">
      <SourceCardHeader title={`Scenarios (${sessions.length})`} />
      <div className="brief-source-card-body">
        <div className="brief-scenarios-list">
          {sessions.map(s => {
            const isActive  = s.id === activeId
            const isDefault = s.id === defaultId
            return (
              <div key={s.id} className={`brief-scenario-row${isActive ? ' active' : ''}`}>
                <div>
                  <div className="u-label-base">
                    {s.is_autonomo ? 'Autónomo' : 'IRPF'} · {s.tax_year}
                    {isDefault && <span className="brief-scenario-default-tag">Default</span>}
                  </div>
                  <div className="u-name-sub">
                    {formatEuro(s.irpf_gross)} ·{' '}
                    {s.is_draft
                      ? <span className="brief-scenario-draft-tag">{getAdvisorSessionStateLabel('draft')}, not published · last edited {formatDate(s.updated_at)}</span>
                      : <>{getAdvisorSessionStateLabel('published')} {formatDate(s.published_at)}</>}
                  </div>
                  {!s.is_draft && s.public_token && (
                    <a
                      href={`/calculator/result/${s.public_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="u-text-link"
                    >
                      View as client saw it ↗
                    </a>
                  )}
                </div>
                <button
                  className="brief-scenario-view-btn"
                  onClick={() => onSelect(isDefault ? null : s.id)}
                  disabled={loading || isActive}
                >
                  {isActive ? '✓ Viewing this' : 'View scenario →'}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function CalculatorColumn({ done, estimate, incomeByOwner, filingComparison, visibleSessions, viewedSessionId, onSelectSession, loadingSession }) {
  if (!done) {
    return (
      <div className="brief-col-primary">
        <div className="brief-source-card">
          <SourceCardHeader title="IRPF Calculator" completed={false} />
          <div className="brief-source-card-body"><p className="brief-empty-note">Not completed yet.</p></div>
        </div>
      </div>
    )
  }
  if (!estimate) {
    return (
      <div className="brief-col-primary">
        <div className="brief-source-card completed">
          <SourceCardHeader title="IRPF Calculator" completed />
          <div className="brief-source-card-body"><p className="brief-empty-note">No calculator estimate available.</p></div>
        </div>
      </div>
    )
  }

  const hasRange = estimate.estimate_low != null && estimate.estimate_high != null
    && estimate.estimate_low !== estimate.estimate_high

  return (
    <div className="brief-col-primary">
      <div className="brief-source-card completed">
        <SourceCardHeader
          title={estimate.is_draft ? 'IRPF Calculator — Draft (unpublished)' : 'IRPF Calculator — Current'}
          sub={estimate.ran_at
            ? (estimate.is_draft
                ? `Last edited ${formatDateTime(estimate.ran_at)} · client hasn't saved this; figures may change`
                : `Run on ${formatDateTime(estimate.ran_at)}`)
            : null}
          completed
          badge={estimate.is_draft ? getAdvisorSessionStateLabel('draft') : undefined}
          badgeClass={estimate.is_draft ? 'brief-source-card-badge--draft' : ''}
          link={(!estimate.is_draft && estimate.public_token)
            ? { href: `/calculator/result/${estimate.public_token}`, label: 'View as client saw it ↗' }
            : null}
        />
        <div className="brief-source-card-body">
          <div className="brief-fact-row">
            <span className="brief-fact-label">Estimated IRPF due{estimate.tax_year ? ` (${estimate.tax_year})` : ''}</span>
            <span className="brief-fact-value brief-calc-headline">
              {formatEuro(estimate.irpf_gross)}
              {hasRange && <span className="brief-calc-range"> ({formatEuro(estimate.estimate_low)}–{formatEuro(estimate.estimate_high)})</span>}
            </span>
          </div>
          {estimate.display_mode === 'adjustment_estimate' && (
            <div className="brief-adjustment-baseline-note">
              This is a conservative baseline — see potential reductions below.
            </div>
          )}

          <FilingComparisonNote comparison={filingComparison} />

          {incomeByOwner
            ? incomeByOwner.map(ownerGroup => (
                <div key={ownerGroup.owner} className={ownerGroup.label ? `brief-income-owner-block brief-income-owner-block--${ownerGroup.owner}` : undefined}>
                  {ownerGroup.label && <div className="brief-income-owner-heading">{ownerGroup.label}</div>}
                  {ownerGroup.groups.map(g => (
                    <div key={g.base} className="brief-income-group">
                      <div className="brief-fact-group-title">{BASE_LABELS[g.base] || g.base}</div>
                      <GroupedIncomeTable items={g.items} />
                    </div>
                  ))}
                </div>
              ))
            : <p className="brief-empty-note">No income items on this run.</p>}

          {estimate.display_mode === 'adjustment_estimate' && (
            <AdjustmentItemsTable items={estimate.adjustment_items} />
          )}
        </div>
      </div>
      <ScenariosCard
        sessions={visibleSessions}
        viewedSessionId={viewedSessionId}
        onSelect={onSelectSession}
        loading={loadingSession}
      />
    </div>
  )
}

// ── Context column — narrow, Snapshot + profile ─────────────────────────────
// Profile facts aren't attributed to one product (either product's
// claim-session gap-fill can populate them). Assets ARE Snapshot-specific
// — the calculator flow never asks about them — so Spain Snapshot is now a
// pure asset view; filing history moved to the header (2026-08 feedback).
// Income facts only render here as a fallback when there's no calculator
// run to supersede them. Data conflicts get their own card below Snapshot
// — moved out of the Calculator card (2026-08 feedback): a conflict is a
// fact about the RELATIONSHIP between the confirmed profile and a
// calculator run, not something either source owns on its own, so it
// doesn't really belong inside either column's source card.

function DataConflictsCard({ conflicts }) {
  if (!conflicts || conflicts.length === 0) return null
  return (
    <div className="brief-source-card">
      <SourceCardHeader title="Data conflicts" sub="Confirmed profile vs. latest calculator run" />
      <div className="brief-source-card-body">
        <div className="brief-unconfirmed-list">
          {conflicts.map((c, i) => (
            <div key={i} className="brief-unconfirmed-row">
              <span className="brief-unconfirmed-fact"><strong>{c.field.replace(/_/g, ' ')}</strong></span>
              <span className="brief-unconfirmed-why">
                Confirmed: {String(c.confirmed_value)} · Calculator: {String(c.calculator_value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Profile box removed (ticket 008, D1) — every fact it used to show now
// lives once, as its own row, in the header's merged summaryRows.

function ContextColumn({ snapshotDone, assetFacts, incomeFacts, showIncomeFallback, dataConflicts, triagePublicToken }) {
  const hasSnapshotContent = assetFacts.length > 0 || (showIncomeFallback && incomeFacts.length > 0)

  return (
    <div className="brief-col-context">
      <div className={`brief-source-card${snapshotDone ? ' completed' : ''}`}>
        <SourceCardHeader
          title="Spain Snapshot"
          completed={snapshotDone}
          link={(snapshotDone && triagePublicToken)
            ? { href: `/spainsnapshot/results/${triagePublicToken}`, label: 'View as client saw it ↗' }
            : null}
        />
        <div className="brief-source-card-body">
          {!snapshotDone ? (
            <p className="brief-empty-note">Not completed yet.</p>
          ) : hasSnapshotContent ? (
            <>
              {assetFacts.length > 0 && <FactGroup title="Assets" facts={assetFacts} />}
              {showIncomeFallback && incomeFacts.length > 0 && <FactGroup title="Income sources" facts={incomeFacts} />}
            </>
          ) : (
            <p className="brief-empty-note">No additional facts beyond the confirmed profile.</p>
          )}
        </div>
      </div>

      <DataConflictsCard conflicts={dataConflicts} />
    </div>
  )
}

// ── Tax work — Setup / Spain filings, one section ───────────────────────────
// US coordination removed entirely (2026-08 feedback) — everything it
// showed was cross-border context, not a Spain-side task or form; dropping
// it rather than relocating anything. Missing-info gaps are condensed to a
// single terse line rather than a standalone section — true per-item
// placement (e.g. "RETA — totalization status unknown" attached to that
// specific row) needs the data_gaps→obligation link confirmed first; see
// top-of-file note.

function TaxWorkSection({ setupTasks, spainFindings, unconfirmedFacts }) {
  return (
    <BriefSection title="Tax work">
      {setupTasks.length > 0 && (
        <FactGroup title="Setup" facts={setupTasks} />
      )}

      <div className="brief-fact-group-title brief-tax-work-subheading">Spain filings</div>
      <FindingsTable findings={spainFindings} />
      {unconfirmedFacts.length > 0 && (
        <div className="brief-tax-work-note">
          Unconfirmed: {unconfirmedFacts.map(g => g.label).join(', ')}
        </div>
      )}
    </BriefSection>
  )
}

function CaseHeader({ header, generatedAt, onRegenerate, regenerating }) {
  return (
    <div className="brief-header">
      <div className="brief-header-top">
        <div>
          <h1 className="brief-header-name">{header.name}</h1>
          <div className="u-row-md">
            {header.email && <EmailDisplay email={header.email} />}
            {header.phone && <PhoneDisplay phone={header.phone} />}
          </div>
        </div>
        <div className="brief-header-right">
          <div className="brief-header-right-row">
            <span className="brief-header-right-label">Pipeline:</span>
            {statusLabel(header.leadStatus)}
          </div>
          <div className="brief-header-right-row">
            <span className="brief-header-right-label">Assigned:</span>
            {formatDate(header.assignedAt)}
          </div>
        </div>
      </div>

      <div className="brief-summary-grid">
        {header.summaryRows.map(({ label, value }) => (
          <React.Fragment key={label}>
            <div className="brief-summary-label">{label}</div>
            <div className="brief-summary-value">{value}</div>
          </React.Fragment>
        ))}
      </div>

      <div className="brief-header-footer-row">
        <span className="brief-header-right-row brief-header-generated">
          Refreshed {formatDateTime(generatedAt)}
        </span>
        <button className="brief-regen-btn" onClick={onRegenerate} disabled={regenerating}>
          {regenerating ? 'Refreshing…' : '↺ Refresh'}
        </button>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function CaseBriefPage() {
  const { subjectId } = useParams()
  const navigate      = useNavigate()
  const { user }      = useAuth()

  const [subject,     setSubject]     = useState(null)
  const [profileData, setProfileData] = useState({ tp: null, yp: null })
  const [email,       setEmail]       = useState(null)
  const [phone,       setPhone]       = useState(null)
  const [triagePublicToken, setTriagePublicToken] = useState(null)
  const [loading,     setLoading]     = useState(true)
  const [generating,  setGenerating]  = useState(false)
  const [regenerating,setRegenerating]= useState(false)
  const [error,       setError]       = useState(null)
  const [viewedSessionId, setViewedSessionId] = useState(null)
  const [viewedBrief,     setViewedBrief]     = useState(null)
  // Ticket 071: the server said there is no case brief to make because this client only
  // shared an intake (generate-case-brief: { brief: null, reason: 'intake_only' }).
  const [intakeOnly,      setIntakeOnly]      = useState(false)

  const fetchAdvisorSubject = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) throw new Error('Not authenticated')

    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-advisor-subject?subject_id=${subjectId}`,
      { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }
    )
    const body = await res.json()
    if (!res.ok) throw new Error(body.error || 'Failed to load subject')
    return body.subject
  }, [subjectId])

  const loadData = useCallback(async () => {
    if (!user || !subjectId) return
    setLoading(true)
    setError(null)

    try {
      const sub = await fetchAdvisorSubject()
      setSubject(sub)

      if (sub.kind === 'lead') {
        setProfileData({ tp: sub.tp || null, yp: sub.yp || null })
        setEmail(sub.email || null)
        setPhone(sub.phone || null)
        setTriagePublicToken(sub.triage_public_token || null)
      }
    } catch (err) {
      setError(err.message || 'Subject not found.')
    }

    setLoading(false)
  }, [user, subjectId, fetchAdvisorSubject])

  useEffect(() => { loadData() }, [loadData])

  // Last visible-session fingerprint we auto-regenerated for (loop guard).
  const regenKeyRef = useRef(null)

  useEffect(() => {
    if (subject?.kind !== 'lead' || generating) return
    // An intake-only client has nothing to generate; do not ask again on every render.
    if (!subject.case_brief && intakeOnly) return
    if (!subject.case_brief) { generateBrief(false); return }
    // Cached brief may be stale: the default (newest visible) session can be
    // an unpublished draft that has since been edited, published or deleted.
    // Regenerating is cheap (deterministic, no model call). Loop guard:
    // regenerating does not refetch the subject, so visible_sessions can stay
    // stale and setSubject would re-fire this effect. regenKeyRef limits
    // auto-regeneration to once per observed visible[0] (id + updated_at); a
    // genuinely newer draft has a new key and regenerates again.
    const current = subject.visible_sessions?.[0] || null
    const est = subject.case_brief.calculator_estimate || null
    const stale = current
      ? (!est || est.session_id !== current.id || (current.updated_at && est.ran_at !== current.updated_at))
      : !!est
    if (!stale) return
    const key = `${subjectId}|${current?.id ?? 'none'}|${current?.updated_at ?? ''}`
    if (regenKeyRef.current === key) return
    regenKeyRef.current = key
    generateBrief(false)
  }, [subject]) // intentional: only trigger when subject changes

  async function generateBrief(isRegen = false, peekSessionId = null) {
    if (isRegen) setRegenerating(true)
    else         setGenerating(true)
    setError(null)

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Not authenticated')

      // Explicit "Refresh" re-fetches the subject itself (products/tp/yp/
      // visible_sessions) alongside regenerating the brief — see the
      // 2026-08 fix note. Skipped for peeks and the first-load
      // auto-generate (subject is already fresh there).
      const subjectRefetch = (isRegen && !peekSessionId)
        ? fetchAdvisorSubject().catch(() => null)
        : null

      const res  = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-case-brief`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            tax_subject_id: subjectId,
            ...(peekSessionId ? { calculator_session_id: peekSessionId } : {}),
          }),
        }
      )
      const json = await res.json()
      if (res.status === 403) throw new Error('Partner-data-share consent is no longer on record for this subject.')
      if (res.ok && !json.brief && json.reason === 'intake_only') {
        // Not an error: the Shared intake panel is the content for this client.
        setIntakeOnly(true)
        return
      }
      if (!res.ok || !json.brief) throw new Error(json.error || 'Brief generation failed')

      const refreshedSubject = subjectRefetch ? await subjectRefetch : null

      if (peekSessionId) {
        setViewedBrief(json.brief)
      } else {
        setSubject(prev => ({
          ...prev,
          ...(refreshedSubject?.kind === 'lead' ? refreshedSubject : {}),
          case_brief: json.brief,
          case_brief_generated_at: json.brief.generated_at,
        }))
        if (refreshedSubject?.kind === 'lead') {
          setProfileData({ tp: refreshedSubject.tp || null, yp: refreshedSubject.yp || null })
          setEmail(refreshedSubject.email || null)
          setPhone(refreshedSubject.phone || null)
          setTriagePublicToken(refreshedSubject.triage_public_token || null)
        }
        setViewedBrief(null)
        setViewedSessionId(null)
      }

      if (!refreshedSubject && !profileData.yp && json.brief.tax_year) {
        const refreshed = await fetchAdvisorSubject()
        if (refreshed.kind === 'lead' && refreshed.yp) {
          setProfileData(prev => ({ ...prev, yp: refreshed.yp }))
        }
      }
    } catch (err) {
      setError(err.message || 'Refresh failed.')
    } finally {
      setGenerating(false)
      setRegenerating(false)
    }
  }

  function viewPublishedSession(sessionId) {
    if (sessionId === null) {
      setViewedSessionId(null)
      setViewedBrief(null)
      return
    }
    setViewedSessionId(sessionId)
    generateBrief(false, sessionId)
  }

  const activeBrief = viewedBrief || subject?.case_brief || null

  const sections = useMemo(() => {
    if (!activeBrief || !subject) return null
    return buildSections(activeBrief, subject, profileData.tp, profileData.yp, email, phone, triagePublicToken)
  }, [activeBrief, subject, profileData, email, phone, triagePublicToken])

  if (loading) {
    return (
      <div className="brief-page">
        <div className="brief-generating">
          <div className="brief-generating-sub">Loading…</div>
        </div>
      </div>
    )
  }

  if (!subject) {
    return (
      <div className="brief-page">
        <button className="brief-back" onClick={() => navigate('/advisor')}>← Back to dashboard</button>
        <p className="brief-error-text">Subject not found.</p>
      </div>
    )
  }

  if (subject.kind === 'referral_placeholder') {
    return (
      <div className="brief-page">
        <button className="brief-back" onClick={() => navigate('/advisor')}>← Back to dashboard</button>

        <div className="brief-placeholder">
          <div className="brief-placeholder-title">Referral in progress</div>
          {subject.masked_email && (
            <div className="u-name-sub">{subject.masked_email}</div>
          )}
          <p className="brief-placeholder-text">
            This person was referred to your firm
            {subject.source_product ? ` via ${getAdvisorProductLabel(subject.source_product)}` : ''},
            but hasn't yet agreed to share their tax details with you. No name, contact
            information, or tax facts are available until they do.
          </p>
          <div className="brief-header-right-row">
            <span className="brief-header-right-label">Pipeline:</span>
            {statusLabel(subject.lead_status)}
          </div>
          <div className="brief-header-right-row">
            <span className="brief-header-right-label">Assigned:</span>
            {formatDate(subject.lead_assigned_at)}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="brief-page">
      <button className="brief-back" onClick={() => navigate('/advisor')}>← Back to dashboard</button>

      {error && (
        <div className="brief-error">
          <span>{error}</span>
          <button className="brief-error-retry" onClick={() => generateBrief(false)}>Retry</button>
        </div>
      )}

      {generating && !subject.case_brief && (
        <div className="brief-generating">
          <div className="brief-generating-title">{subject.display_name}</div>
          <div className="brief-generating-dots">
            {[0,1,2].map(i => (
              <div key={i} className="brief-generating-dot" style={{ animationDelay: `${i * 0.2}s` }} />
            ))}
          </div>
          <div className="brief-generating-sub">Loading profile facts…</div>
        </div>
      )}

      {subject.intake && !sections && !generating && (
        <>
          <div className="brief-header">
            <div className="brief-header-top">
              <div>
                <h1 className="brief-header-name">{subject.display_name || 'Unnamed subject'}</h1>
                {intakeOnly && <p className="u-hint">{ADVISOR_INTAKE_ONLY_NOTE}</p>}
              </div>
              <div className="brief-header-right">
                <div className="brief-header-right-row">
                  <span className="brief-header-right-label">Pipeline:</span>
                  {statusLabel(subject.lead_status)}
                </div>
                <div className="brief-header-right-row">
                  <span className="brief-header-right-label">Assigned:</span>
                  {formatDate(subject.lead_assigned_at)}
                </div>
              </div>
            </div>
          </div>
          <AdvisorIntakePanel intake={subject.intake} email={email} phone={phone} />
        </>
      )}

      {sections && (
        <>
          <CaseHeader
            header={sections.header}
            generatedAt={subject.case_brief_generated_at}
            onRegenerate={() => generateBrief(true)}
            regenerating={regenerating}
          />

          {subject.intake && (
            <AdvisorIntakePanel intake={subject.intake} email={email} phone={phone} showContact={false} />
          )}

          {viewedSessionId && (
            <div className="brief-peek-note">
              Showing the estimate and data conflicts from a different scenario below — everything else on this page reflects the confirmed profile and doesn't change based on which scenario you're viewing.
            </div>
          )}

          <div className="brief-columns">
            <CalculatorColumn
              done={sections.products.calculator}
              estimate={sections.calculatorEstimate}
              incomeByOwner={sections.incomeByOwner}
              filingComparison={sections.filingComparison}
              visibleSessions={subject.visible_sessions}
              viewedSessionId={viewedSessionId}
              onSelectSession={viewPublishedSession}
              loadingSession={generating}
            />
            <ContextColumn
              snapshotDone={sections.products.triage}
              assetFacts={sections.assetFacts}
              incomeFacts={sections.incomeFacts}
              showIncomeFallback={!sections.calculatorEstimate}
              dataConflicts={sections.dataConflicts}
              triagePublicToken={sections.header.triagePublicToken}
            />
          </div>

          <TaxWorkSection
            setupTasks={sections.setupTasks}
            spainFindings={sections.spainFindings}
            unconfirmedFacts={sections.unconfirmedFacts}
          />

          {sections.advisorNotes && (
            <BriefSection title="Client notes">
              <p className="brief-advisor-text">{sections.advisorNotes}</p>
            </BriefSection>
          )}
        </>
      )}
    </div>
  )
}